import { getActiveLocale } from "../locale";

const enContent = {
  appLinks: {
    ios: "https://apps.apple.com/us/app/stalkie-follow-alerts/id6758714223",
    android:
      "https://play.google.com/store/apps/details?id=com.viniciusmoreria.stalkie.app",
  },
  seo: {
    title: "Stalkie — Instagram Follow Tracker | See Recent Follows",
    description:
      "Instagram follow tracker for seeing who a public profile recently followed. Compare activity over time without sharing an Instagram password.",
    keywords: [
      "track recent Instagram follows",
      "Instagram follow tracker",
      "see who someone recently followed",
      "Instagram unfollow tracker",
      "track Instagram activity",
      "cheating follow tracker",
      "crush follow tracker",
    ],
    canonical: "https://stalkie.app/",
    ogTitle: "Stalkie — Instagram Follow Tracker | See Recent Follows",
    ogDescription:
      "Instagram follow tracker for seeing who a public profile recently followed. Compare activity over time without sharing an Instagram password.",
    ogImage: "https://stalkie.app/og.png",
    twitterCard: "summary_large_image",
  },
  header: {
    logo: "Stalkie",
    links: [
      { label: "How it works", href: "#how-it-works" },
      { label: "Reviews", href: "#reviews" },
      { label: "FAQ", href: "#faq" },
    ],
    loginLabel: "Login",
    ctaLabel: "Get Started",
  },
  hero: {
    badge: "100% ANONYMOUS TRACKING",
    tiktokBadge: "as seen on tiktok",
    liveText: "people searching right now",
    title: "See who they recently followed on Instagram",
    subtitle:
      "Find out who your partner, crush, or friends followed recently. Get alerts on new activity in a clean timeline. No login required.",
    inputPlaceholder: "@ username",
    ctaLabel: "Search",
    helperText: "No Instagram password required. Search anonymously.",
    trustBullets: [
      "100% Anonymous",
      "No login required",
      "Activity timeline",
      "Spot suspicious activity",
    ],
    socialProof: "Thousands of searches made",
  },
  // Qualitative metrics (Opção segura)
  qualitativeMetrics: [
    { label: "Fast profile search" },
    { label: "No password required" },
    { label: "Clean timeline" },
  ],
  metrics: {
    publicSearches: "Public Searches",
    countries: "Countries",
    passwordsRequired: "Passwords Required",
  },
  howItWorks: {
    eyebrow: "How it works",
    title: "Catch every move, automatically.",
    subtitle: "How Stalkie tracks activity without your login.",
    step2: {
      scanning: "Scanning...",
      newBadge: "NEW",
      timeAgo: "2h ago",
    },
    step3Events: [
      {
        type: "a",
        avatar: "ER",
        avatarBg: "#FF6B9D",
        time: "Just now",
        text: "Started following @emily.rose",
      },
      {
        type: "b",
        avatar: "JP",
        avatarBg: "#C084FC",
        time: "2 hours ago",
        text: "Unfollowed @jake_photo",
      },
      {
        type: "c",
        avatar: "LF",
        avatarBg: "#34D399",
        time: "Today",
        text: "Started following @lisa_fit",
      },
      {
        type: "a",
        avatar: "SM",
        avatarBg: "#FB923C",
        time: "Yesterday",
        text: "Started following @sofia.m",
      },
      {
        type: "b",
        avatar: "NS",
        avatarBg: "#60A5FA",
        time: "Yesterday",
        text: "Unfollowed @nina.s",
      },
      {
        type: "c",
        avatar: "AT",
        avatarBg: "#34D399",
        time: "2 days ago",
        text: "Started following @alex_t",
      },
    ],
    steps: [
      {
        badge: "STEP 1",
        title: "Enter their username",
        description: "Just type the Instagram username you want to check.",
      },
      {
        badge: "STEP 2",
        title: "Reveal recent follows",
        description:
          "We map out their newest connections in an easy-to-read timeline.",
      },
      {
        badge: "STEP 3",
        title: "Get alerts on changes",
        description:
          "Be the first to know when they follow or unfollow someone.",
      },
    ],
  },
  productPreview: {
    title: "Spot the red flags instantly",
    subtitle:
      "Get a clean timeline showing who they followed, when, and their profile details.",
    profile: {
      handle: "@username",
      type: "Activity Feed",
    },
    tabs: ["Recent", "Following", "Followers"],
    events: [
      {
        badge: "New Follow",
        title: "Started following @katie.xo",
        time: "Just now",
        avatar: "KX",
      },
      {
        badge: "New Follow",
        title: "Started following @lucas_fit",
        time: "10m ago",
        avatar: "LF",
      },
      {
        badge: "Unfollow",
        title: "Unfollowed @fitness_coach",
        time: "2h ago",
        avatar: "FC",
      },
      {
        badge: "Alert",
        title: "Following count changed (+2)",
        time: "Today",
        avatar: "FC",
      },
      {
        badge: "Timeline",
        title: "Timeline refreshed",
        time: "2 days ago",
        avatar: "TR",
      },
    ],
    disclaimer:
      "We only check public profile activity. Private profiles cannot be monitored.",
  },
  socialIntent: {
    title: "The questions you've been asking yourself",
    subtitle: "Stalkie gives you the answers you deserve in seconds.",
    cards: [
      {
        title: "Who is that new follow?",
        text: "Spot recent follows instantly without checking Instagram all day.",
      },
      {
        title: "Did they follow and then unfollow?",
        text: "Keep track of count fluctuations and hidden activity.",
      },
      {
        title: "Is your partner hiding something?",
        text: "Get proof of who they followed recently, with timestamps.",
      },
      {
        title: "What changed since yesterday?",
        text: "Compare their profile activity today vs. yesterday automatically.",
      },
      {
        title: "Are they talking to someone new?",
        text: "Get notified when they make a new connection.",
      },
      {
        title: "Stop guessing the truth",
        text: "Get a clear timeline instead of trying to remember their follow lists.",
      },
    ],
  },
  timeline: {
    title: "A timeline of who they follow",
    subtitle: "Recent activity organized by profile and time.",
    events: [
      {
        badge: "NEW FOLLOW",
        title: "Started following @katie.xo",
        time: "2m ago",
        avatar: "KX",
      },
      {
        badge: "NEW FOLLOW",
        title: "Started following @lucas_fit",
        time: "15m ago",
        avatar: "LF",
      },
      {
        badge: "COUNT UPDATE",
        title: "Following count changed",
        time: "1h ago",
        avatar: "FC",
      },
      {
        badge: "UNFOLLOW",
        title: "Unfollowed @fitness_coach",
        time: "4h ago",
        avatar: "FC",
      },
      {
        badge: "TIMELINE",
        title: "Refreshed today",
        time: "5h ago",
        avatar: "RT",
      },
    ],
  },
  socialProofGrid: {
    title: "People check Stalkie when something feels off",
    subtitle: "Real situations. Clear timelines. Stop guessing.",
    reviews: [
      {
        name: "Maya",
        text: "I just wanted to know what changed. The timeline made it clear without logging into Instagram.",
        rating: 5,
        badge: "",
      },
      {
        name: "Alex T.",
        text: "Way easier than checking manually.",
        rating: 5,
        badge: "",
      },
      {
        name: "Jess R.",
        text: "I found the recent follows in seconds.",
        rating: 5,
        badge: "",
      },
      {
        name: "Mike P.",
        text: "No login needed, so I trusted it.",
        rating: 5,
        badge: "",
      },
      {
        name: "Emily L.",
        text: "Clean and straight to the point.",
        rating: 5,
        badge: "",
      },
      {
        name: "David C.",
        text: "Better than refreshing Instagram over and over.",
        rating: 4,
        badge: "",
      },
      {
        name: "Lucas B.",
        text: "I could finally stop guessing.",
        rating: 5,
        badge: "",
      },
      {
        name: "Amanda K.",
        text: "It organized everything I was trying to check.",
        rating: 5,
        badge: "",
      },
      {
        name: "Clara V.",
        text: "Simple search, clear result.",
        rating: 4,
        badge: "",
      },
      {
        name: "Thiago F.",
        text: "The public timeline was enough.",
        rating: 5,
        badge: "",
      },
      {
        name: "Sophia G.",
        text: "I liked that it never asked for my password.",
        rating: 5,
        badge: "",
      },
      {
        name: "Rafael S.",
        text: "Way better than checking profiles one by one.",
        rating: 4,
        badge: "",
      },
    ],
  },
  benefits: {
    title: "Stop guessing. Know the truth.",
    subtitle:
      "Automatic tracking designed for relationships, creators, and curiosity.",
    cards: [
      {
        title: "Spot suspicious follows",
        description: "Catch new connections as they happen in a simple feed.",
      },
      {
        title: "Catch secret unfollows",
        description: "See when they clean up their list or remove someone.",
      },
      {
        title: "Follower updates",
        description: "Keep track of any follower changes.",
      },
      {
        title: "Real-time notifications",
        description: "Receive updates when activity changes.",
      },
      {
        title: "Zero effort",
        description: "No more taking manual screenshots or comparing lists.",
      },
    ],
  },
  comparison: {
    eyebrow: "Compare",
    title: "How Stalkie compares to manual stalking",
    subtitle: "Stop checking manually and let Stalkie handle it.",
    columns: ["Feature", "Stalkie", "Manual"],
    rows: [
      ["Completely Anonymous", "Yes, Always", "No / Unclear"],
      ["Instagram Login", "Not Required", "Required"],
      ["Suspicious Follow Detection", "Automated", "Manual Guessing"],
      ["Follow Order", "Newest first", "Random by IG"],
      ["Gender Breakdown", "Included", "Not available"],
      ["Updates", "Every Hour", "Manual Refresh"],
      ["Notifications", "Instant Push", "None"],
      ["Profiles monitored", "Up to 3", "1 at a time"],
      ["Ads", "No Ads", "Ads Everywhere"],
      ["Devices", "iOS, Android & Web", "PC Only"],
    ],
  },
  privacy: {
    title: "100% Anonymous. No Password Required.",
    subtitle:
      "Stalkie is built for safe follow checks. We never ask for your Instagram login details.",
    items: [
      {
        title: "No login required",
        description: "Search anonymously without connecting your Instagram.",
      },
      {
        title: "Secure & private",
        description: "Your target will never know you checked.",
      },
      {
        title: "Profile rules",
        description: "Only public profile activity can be tracked.",
      },
    ],
    helperText:
      "Private accounts cannot be tracked. We only monitor publicly available follow lists.",
  },
  faq: {
    title: "Frequently asked questions",
    subtitle: "Frequently asked questions about Stalkie search.",
    items: [
      {
        question: "Do I need to log in with Instagram?",
        answer:
          "No. Stalkie never asks for your Instagram password or account details.",
      },
      {
        question: "Can Stalkie check private accounts?",
        answer:
          "No. Stalkie only works with public Instagram profiles. Private profiles are secure.",
      },
      {
        question: "Will they know I am tracking them?",
        answer:
          "No. Stalkie is 100% anonymous. We check profiles from our own servers, so they will never be notified.",
      },
      {
        question: "What can I track?",
        answer:
          "You can check recent follow changes, unfollows, and follower count changes in one place.",
      },
      {
        question: "Why are some results unavailable?",
        answer:
          "Availability depends on Instagram's data servers. If a profile cannot be checked, we'll show you immediately.",
      },
      {
        question: "Do you store my Instagram password?",
        answer:
          "No. We never ask for your password, so we have nothing to store.",
      },
    ],
  },
  trending: {
    eyebrow: "Trending",
    title: "The word is spreading",
    subtitle:
      "Thousands of people are checking public profiles every day with Stalkie.",
    quotes: [
      {
        platform: "instagram",
        user: "@maya_c",
        text: "Didn't know public follows worked like this. Clean and fast.",
      },
      {
        platform: "tiktok",
        user: "@jess.r",
        text: "No login needed. Searched in 10 seconds flat.",
      },
      {
        platform: "twitter",
        user: "@alex_t",
        text: "Finally something that doesn't ask for my password.",
      },
      {
        platform: "instagram",
        user: "@luca_b",
        text: "The timeline sorted everything I was trying to track.",
      },
      {
        platform: "tiktok",
        user: "@sofia.m",
        text: "Public activity only. I actually trust this.",
      },
      {
        platform: "twitter",
        user: "@mike_p",
        text: "Way easier than refreshing manually.",
      },
      {
        platform: "instagram",
        user: "@liam.k",
        text: "Told my friend about it and she caught her bf following his ex in 2 mins.",
      },
      {
        platform: "tiktok",
        user: "@clara_v",
        text: "The unfollow tracker is wild. Found out who removed me instantly.",
      },
      {
        platform: "twitter",
        user: "@dan_s",
        text: "No extensions, no weird downloads. Just searched and got the results.",
      },
      {
        platform: "instagram",
        user: "@emily_rose",
        text: "I check this daily now just to see who follows who. So addicting.",
      },
      {
        platform: "tiktok",
        user: "@hannah.b",
        text: "Actually works and doesn't spam you with notifications.",
      },
      {
        platform: "twitter",
        user: "@sam_w",
        text: "Best tool to see public activity without logging in.",
      },
    ],
  },
  finalCta: {
    title: "Ready to know the truth?",
    subtitle:
      "Enter their username below to reveal recent follow changes and catch suspicious activity.",
    inputPlaceholder: "@ username",
    ctaLabel: "Search",
    trustBullets: ["No Instagram login", "100% Anonymous", "Clear timeline"],
  },
  guides: {
    eyebrow: "Guides",
    title: "Learn more about Instagram activity",
    subtitle:
      "Practical guides to understand follows, unfollows and public profile monitoring.",
    readMore: "Read guide",
    items: [
      {
        title: "How to see who someone recently followed on Instagram",
        description:
          "Understand what Instagram shows and how to track new follows over time.",
        href: "/how-to-see-who-someone-followed-on-instagram/",
      },
      {
        title: "Instagram follow tracker",
        description:
          "Learn how a follow tracker detects changes without requiring your Instagram login.",
        href: "/instagram-follow-tracker/",
      },
      {
        title: "Track Instagram following activity anonymously",
        description:
          "See how anonymous monitoring works for public Instagram profiles.",
        href: "/track-instagram-following-activity/",
      },
      {
        title: "Anonymous Instagram tracker",
        description:
          "A privacy-focused guide to monitoring public profile activity.",
        href: "/anonymous-instagram-tracker/",
      },
      {
        title: "How to get notifications for new Instagram follows",
        description:
          "Understand follow alerts and monitor public profile changes over time.",
        href: "/instagram-follow-notifications/",
      },
      {
        title: "Instagram unfollow tracker",
        description:
          "Learn what unfollow tracking can actually verify from public data.",
        href: "/instagram-unfollow-tracker/",
      },
      {
        title: "Track changes in an Instagram following list",
        description:
          "A practical guide to comparing public following-list snapshots.",
        href: "/instagram-following-list-changes/",
      },
    ],
  },
  footer: {
    description:
      "Stalkie helps you track recent Instagram activity, spot suspicious follows, and stop guessing.",
    productTitle: "Product",
    companyTitle: "Company",
    legalTitle: "Legal",
    allRightsReserved: "All rights reserved.",
    productLinks: [
      { label: "Features", href: "#features" },
      { label: "How it works", href: "#how-it-works" },
      { label: "FAQ", href: "#faq" },
      { label: "Guides", href: "#guides" },
    ],
    companyLinks: [
      { label: "Contact", href: "mailto:support@stalkie.app" },
      { label: "Support", href: "mailto:support@stalkie.app" },
    ],
    legalLinks: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
    ],
    disclaimer:
      "Stalkie is not affiliated with Instagram or Meta. Instagram is a trademark of its respective owner.",
  },
};

const ptContent = {
  appLinks: enContent.appLinks,
  seo: {
    title: "Stalkie — Rastreador de Follows do Instagram | Veja Follows Recentes",
    description:
      "Rastreador de follows do Instagram para ver quem um perfil público seguiu recentemente. Compare a atividade sem compartilhar sua senha.",
    keywords: [
      "rastrear follows recentes instagram",
      "rastreador de follows instagram",
      "ver quem alguém seguiu recentemente",
      "rastreador de unfollows instagram",
      "ver atividades instagram",
    ],
    canonical: "https://stalkie.app/",
    ogTitle: "Stalkie — Rastreador de Follows do Instagram | Veja Follows Recentes",
    ogDescription:
      "Rastreador de follows do Instagram para ver quem um perfil público seguiu recentemente. Compare a atividade sem compartilhar sua senha.",
    ogImage: "https://stalkie.app/og.png",
    twitterCard: "summary_large_image",
  },
  header: {
    logo: "Stalkie",
    links: [
      { label: "Como funciona", href: "#how-it-works" },
      { label: "Avaliações", href: "#reviews" },
      { label: "FAQ", href: "#faq" },
    ],
    loginLabel: "Entrar",
    ctaLabel: "Começar",
  },
  hero: {
    badge: "RASTREAMENTO 100% ANÔNIMO",
    tiktokBadge: "visto no tiktok",
    liveText: "pessoas pesquisando agora",
    title: "Veja quem eles seguiram recentemente no Instagram",
    subtitle:
      "Veja quem seu parceiro, crush ou amigos seguiram recentemente. Receba alertas de novos follows e unfollows. 100% anônimo.",
    inputPlaceholder: "Digite o @usuario",
    ctaLabel: "Analisar",
    helperText: "Sem senha do Instagram. Busca 100% anônima.",
    trustBullets: [
      "100% Anônimo",
      "Sem login ou senha",
      "Linha do tempo",
      "Detecte follows suspeitos",
    ],
    socialProof: "Milhares de buscas realizadas",
  },
  qualitativeMetrics: [
    { label: "Busca rápida" },
    { label: "Sem senha" },
    { label: "Linha do tempo" },
  ],
  metrics: {
    publicSearches: "Buscas Públicas",
    countries: "Países",
    passwordsRequired: "Senhas Requeridas",
  },
  howItWorks: {
    eyebrow: "Como funciona",
    title: "Rastreie cada passo, automaticamente.",
    subtitle: "Como o Stalkie monitora as conexões sem precisar do seu login.",
    step2: {
      scanning: "Rastreando...",
      newBadge: "NOVO",
      timeAgo: "há 2h",
    },
    step3Events: [
      {
        type: "a",
        avatar: "ER",
        avatarBg: "#FF6B9D",
        time: "Agora mesmo",
        text: "Começou a seguir @emily.rose",
      },
      {
        type: "b",
        avatar: "JP",
        avatarBg: "#C084FC",
        time: "Há 2 horas",
        text: "Deixou de seguir @jake_photo",
      },
      {
        type: "c",
        avatar: "LF",
        avatarBg: "#34D399",
        time: "Hoje",
        text: "Começou a seguir @lisa_fit",
      },
      {
        type: "a",
        avatar: "SM",
        avatarBg: "#FB923C",
        time: "Ontem",
        text: "Começou a seguir @sofia.m",
      },
      {
        type: "b",
        avatar: "NS",
        avatarBg: "#60A5FA",
        time: "Ontem",
        text: "Deixou de seguir @nina.s",
      },
      {
        type: "c",
        avatar: "AT",
        avatarBg: "#34D399",
        time: "Há 2 dias",
        text: "Começou a seguir @alex_t",
      },
    ],
    steps: [
      {
        badge: "PASSO 1",
        title: "Digite o @usuario",
        description: "Informe o perfil do Instagram que você deseja analisar.",
      },
      {
        badge: "PASSO 2",
        title: "Veja follows recentes",
        description:
          "Nosso sistema mapeia as conexões mais recentes em uma linha do tempo clara.",
      },
      {
        badge: "PASSO 3",
        title: "Receba alertas de mudanças",
        description:
          "Fique sabendo imediatamente sempre que o perfil seguir ou deixar de seguir alguém.",
      },
    ],
  },
  productPreview: {
    title: "Detecte sinais de alerta instantaneamente",
    subtitle:
      "Obtenha uma linha do tempo limpa mostrando quem eles seguiram, quando e os detalhes do perfil.",
    profile: {
      handle: "@usuario",
      type: "Feed de Atividades",
    },
    tabs: ["Recentes", "Seguindo", "Seguidores"],
    events: [
      {
        badge: "Novo Follow",
        title: "Começou a seguir @katie.xo",
        time: "Agora mesmo",
        avatar: "KX",
      },
      {
        badge: "Novo Follow",
        title: "Começou a seguir @lucas_fit",
        time: "Há 10 min",
        avatar: "LF",
      },
      {
        badge: "Unfollow",
        title: "Deixou de seguir @fitness_coach",
        time: "Há 2h",
        avatar: "FC",
      },
      {
        badge: "Alerta",
        title: "Quantidade de seguindo mudou (+2)",
        time: "Hoje",
        avatar: "FC",
      },
      {
        badge: "Linha do tempo",
        title: "Linha do tempo atualizada",
        time: "Há 2 dias",
        avatar: "TR",
      },
    ],
    disclaimer:
      "Apenas checamos perfis públicos. Perfis privados não podem ser monitorados.",
  },
  socialIntent: {
    title: "As perguntas que você tem se feito",
    subtitle: "O Stalkie te dá as respostas que você merece em segundos.",
    cards: [
      {
        title: "Quem é esse novo follow?",
        text: "Veja novos follows instantaneamente sem precisar ficar olhando o Instagram o dia todo.",
      },
      {
        title: "Seguiu e depois deixou de seguir?",
        text: "Monitore flutuações e atividades ocultas na lista de seguindo.",
      },
      {
        title: "Seu parceiro está escondendo algo?",
        text: "Tenha provas de quem eles seguiram recentemente, com data e hora.",
      },
      {
        title: "O que mudou desde ontem?",
        text: "Compare as atividades do perfil hoje versus ontem de forma automática.",
      },
      {
        title: "Estão conversando com alguém novo?",
        text: "Seja notificado sempre que uma nova conexão for feita.",
      },
      {
        title: "Chega de tentar adivinhar",
        text: "Tenha uma linha do tempo clara ao invés de tentar lembrar de cabeça as listas de seguindo.",
      },
    ],
  },
  timeline: {
    title: "Uma linha do tempo de quem eles seguem",
    subtitle: "Atividades recentes organizadas por perfil e data.",
    events: [
      {
        badge: "NOVO FOLLOW",
        title: "Começou a seguir @katie.xo",
        time: "Há 2 min",
        avatar: "KX",
      },
      {
        badge: "NOVO FOLLOW",
        title: "Começou a seguir @lucas_fit",
        time: "Há 15 min",
        avatar: "LF",
      },
      {
        badge: "ATUALIZAÇÃO",
        title: "Quantidade de seguindo mudou",
        time: "Há 1h",
        avatar: "FC",
      },
      {
        badge: "UNFOLLOW",
        title: "Deixou de seguir @fitness_coach",
        time: "Há 4h",
        avatar: "FC",
      },
      {
        badge: "LINHA DO TEMPO",
        title: "Atualizada hoje",
        time: "Há 5h",
        avatar: "RT",
      },
    ],
  },
  socialProofGrid: {
    title: "As pessoas buscam no Stalkie quando sentem que algo está errado",
    subtitle: "Situações reais. Linhas do tempo claras. Pare de adivinhar.",
    reviews: [
      {
        name: "Maya",
        text: "Eu só queria saber o que mudou. A linha do tempo deixou tudo claro sem eu precisar entrar no Instagram.",
        rating: 5,
        badge: "",
      },
      {
        name: "Alex T.",
        text: "Muito mais fácil do que ficar checando manualmente.",
        rating: 5,
        badge: "",
      },
      {
        name: "Jess R.",
        text: "Achei os follows recentes em segundos.",
        rating: 5,
        badge: "",
      },
      {
        name: "Mike P.",
        text: "Não precisa de login, por isso confiei.",
        rating: 5,
        badge: "",
      },
      {
        name: "Emily L.",
        text: "Limpo e direto ao ponto.",
        rating: 5,
        badge: "",
      },
      {
        name: "David C.",
        text: "Melhor do que ficar atualizando o Instagram toda hora.",
        rating: 4,
        badge: "",
      },
      {
        name: "Lucas B.",
        text: "Finalmente pude parar de adivinhar.",
        rating: 5,
        badge: "",
      },
      {
        name: "Amanda K.",
        text: "Organizou tudo o que eu estava tentando rastrear.",
        rating: 5,
        badge: "",
      },
      {
        name: "Clara V.",
        text: "Busca simples, resultado claro.",
        rating: 4,
        badge: "",
      },
      {
        name: "Thiago F.",
        text: "A linha do tempo pública foi suficiente.",
        rating: 5,
        badge: "",
      },
      {
        name: "Sophia G.",
        text: "Adorei que nunca pediu minha senha.",
        rating: 5,
        badge: "",
      },
      {
        name: "Rafael S.",
        text: "Muito melhor do que ficar olhando os perfis um por um.",
        rating: 4,
        badge: "",
      },
    ],
  },
  benefits: {
    title: "Pare de adivinhar. Saiba a verdade.",
    subtitle:
      "Rastreamento automático feito para relacionamentos, criadores e curiosidade.",
    cards: [
      {
        title: "Detecte follows suspeitos",
        description:
          "Veja novas conexões assim que elas acontecem em um feed simples.",
      },
      {
        title: "Veja unfollows secretos",
        description: "Saiba quando eles limpam a lista ou removem alguém.",
      },
      {
        title: "Atualizações de seguidores",
        description: "Acompanhe qualquer mudança na lista de seguidores.",
      },
      {
        title: "Notificações em tempo real",
        description: "Receba alertas assim que houver nova atividade.",
      },
      {
        title: "Zero esforço",
        description:
          "Chega de tirar prints manuais ou ficar comparando listas.",
      },
    ],
  },
  comparison: {
    eyebrow: "Comparar",
    title: "Como o Stalkie se compara à checagem manual",
    subtitle:
      "Pare de olhar manualmente e deixe o Stalkie fazer isso por você.",
    columns: ["Recurso", "Stalkie", "Manual"],
    rows: [
      ["Totalmente Anônimo", "Sim, Sempre", "Não / Duvidoso"],
      ["Login do Instagram", "Não Necessário", "Necessário"],
      ["Detecção de Follows Suspeitos", "Automática", "Adivinhação Manual"],
      ["Ordem dos Follows", "Recentes primeiro", "Aleatório pelo IG"],
      ["Gênero dos Perfis", "Incluído", "Não disponível"],
      ["Atualizações", "A cada hora", "Atualização Manual"],
      ["Notificações", "Push Instantâneo", "Nenhuma"],
      ["Perfis Monitorados", "Até 3 perfis", "1 por vez"],
      ["Anúncios", "Sem Anúncios", "Anúncios em todo lugar"],
      ["Dispositivos", "Qualquer dispositivo", "Apenas no PC"],
    ],
  },
  privacy: {
    title: "100% Anônimo. Sem Senha Necessária.",
    subtitle:
      "O Stalkie é construído para checagens seguras. Nós nunca pediremos seus dados de login do Instagram.",
    items: [
      {
        title: "Sem login necessário",
        description: "Busque anonimamente sem conectar sua conta do Instagram.",
      },
      {
        title: "Seguro & privado",
        description: "O perfil monitorado nunca saberá que você buscou.",
      },
      {
        title: "Regras de perfil",
        description: "Apenas perfis públicos podem ser rastreados.",
      },
    ],
    helperText:
      "Perfis privados não podem ser rastreados. Apenas monitoramos listas públicas.",
  },
  faq: {
    title: "Perguntas frequentes",
    subtitle: "Perguntas frequentes sobre o monitoramento do Stalkie.",
    items: [
      {
        question: "Preciso fazer login com o meu Instagram?",
        answer:
          "Não. O Stalkie nunca pede sua senha ou detalhes da sua conta do Instagram.",
      },
      {
        question: "O Stalkie consegue monitorar perfis privados?",
        answer:
          "Não. O Stalkie funciona apenas com perfis públicos do Instagram. Contas privadas estão protegidas.",
      },
      {
        question: "Eles vão saber que estou monitorando?",
        answer:
          "Não. O Stalkie é 100% anônimo. Fazemos as checagens a partir de nossos servidores, então eles nunca saberão.",
      },
      {
        question: "O que eu consigo rastrear?",
        answer:
          "Você pode ver follows recentes, unfollows recentes e mudanças na contagem de seguidores em um só lugar.",
      },
      {
        question: "Por que alguns resultados estão indisponíveis?",
        answer:
          "A disponibilidade depende dos servidores do Instagram. Se um perfil não puder ser checado, nós te avisaremos na hora.",
      },
      {
        question: "Você guarda a minha senha do Instagram?",
        answer:
          "Não. Nós nunca pedimos a sua senha, portanto não temos o que salvar.",
      },
    ],
  },
  trending: {
    eyebrow: "Tendências",
    title: "O Stalkie é o assunto do momento",
    subtitle:
      "Milhares de pessoas checam perfis públicos todos os dias com o Stalkie.",
    quotes: [
      {
        platform: "instagram",
        user: "@maya_c",
        text: "Não sabia que o rastreio de follows funcionava tão bem. Limpo e rápido.",
      },
      {
        platform: "tiktok",
        user: "@jess.r",
        text: "Sem precisar de login. Buscou em exatos 10 segundos.",
      },
      {
        platform: "twitter",
        user: "@alex_t",
        text: "Finalmente algo que não pede a minha senha do Instagram.",
      },
      {
        platform: "instagram",
        user: "@luca_b",
        text: "A linha do tempo organizou tudo o que eu estava tentando rastrear.",
      },
      {
        platform: "tiktok",
        user: "@sofia.m",
        text: "Apenas atividade pública. Eu realmente confiei nisso.",
      },
      {
        platform: "twitter",
        user: "@mike_p",
        text: "Muito mais fácil do que ficar checando a lista manualmente.",
      },
      {
        platform: "instagram",
        user: "@liam.k",
        text: "Falei para minha amiga e ela pegou o namorado seguindo a ex em 2 minutos.",
      },
      {
        platform: "tiktok",
        user: "@clara_v",
        text: "O rastreador de unfollows é insano. Descobri quem me deu unfollow na hora.",
      },
      {
        platform: "twitter",
        user: "@dan_s",
        text: "Sem extensões ou downloads estranhos. Só busquei e vi os resultados.",
      },
      {
        platform: "instagram",
        user: "@emily_rose",
        text: "Eu checo isso todo dia agora só para ver quem segue quem. Viciante.",
      },
      {
        platform: "tiktok",
        user: "@hannah.b",
        text: "Realmente funciona e não fica te mandando spam.",
      },
      {
        platform: "twitter",
        user: "@sam_w",
        text: "Melhor ferramenta para ver atividade pública sem precisar logar.",
      },
    ],
  },
  finalCta: {
    title: "Pronto para saber a verdade?",
    subtitle:
      "Digite o @usuario abaixo para revelar os follows recentes e detectar atividades suspeitas.",
    inputPlaceholder: "Digite o @usuario",
    ctaLabel: "Analisar",
    trustBullets: [
      "Sem login do Instagram",
      "100% Anônimo",
      "Linha do tempo clara",
    ],
  },
  guides: {
    eyebrow: "Guias",
    title: "Entenda melhor a atividade no Instagram",
    subtitle:
      "Guias práticos para entender follows, unfollows e o monitoramento de perfis públicos.",
    readMore: "Ler guia",
    items: [
      {
        title: "Como ver quem alguém seguiu recentemente no Instagram",
        description:
          "Entenda o que o Instagram mostra e como acompanhar novos follows ao longo do tempo.",
        href: "/pt/como-ver-quem-alguem-seguiu-no-instagram/",
      },
      {
        title: "Rastreador de seguidores do Instagram",
        description:
          "Saiba como detectar mudanças sem fornecer seu login do Instagram.",
        href: "/pt/rastreador-de-seguidores-instagram/",
      },
      {
        title: "Rastrear atividade de seguidos no Instagram",
        description:
          "Veja como funciona o monitoramento anônimo de perfis públicos.",
        href: "/pt/rastrear-atividade-de-seguidos-instagram/",
      },
      {
        title: "Rastreador anônimo de Instagram",
        description:
          "Um guia focado em privacidade para acompanhar atividades públicas.",
        href: "/pt/rastreador-anonimo-instagram/",
      },
      {
        title: "Como receber notificações de novos follows no Instagram",
        description:
          "Entenda os alertas de follow e acompanhe mudanças em perfis públicos.",
        href: "/pt/notificacoes-de-novos-seguidos-instagram/",
      },
      {
        title: "Rastreador de unfollows no Instagram",
        description:
          "Saiba o que o rastreamento de unfollows consegue confirmar em dados públicos.",
        href: "/pt/rastreador-de-unfollows-instagram/",
      },
      {
        title: "Acompanhar mudanças na lista de seguidos do Instagram",
        description:
          "Um guia prático para comparar mudanças em listas públicas de seguidos.",
        href: "/pt/mudancas-na-lista-de-seguidos-instagram/",
      },
    ],
  },
  footer: {
    description:
      "O Stalkie ajuda você a rastrear atividades recentes no Instagram, detectar follows suspeitos e parar de adivinhar.",
    productTitle: "Produto",
    companyTitle: "Empresa",
    legalTitle: "Legal",
    allRightsReserved: "Todos os direitos reservados.",
    productLinks: [
      { label: "Recursos", href: "#features" },
      { label: "Como funciona", href: "#how-it-works" },
      { label: "FAQ", href: "#faq" },
      { label: "Guias", href: "#guides" },
    ],
    companyLinks: [
      { label: "Contato", href: "mailto:support@stalkie.app" },
      { label: "Suporte", href: "mailto:support@stalkie.app" },
    ],
    legalLinks: [
      { label: "Política de Privacidade", href: "/privacy" },
      { label: "Termos de Uso", href: "/terms" },
    ],
    disclaimer:
      "O Stalkie não é afiliado ao Instagram ou Meta. Instagram é uma marca registrada de seus respectivos proprietários.",
  },
};

const esContent = {
  appLinks: enContent.appLinks,
  seo: {
    title: "Stalkie — Rastreador de Follows de Instagram | Mira Follows Recientes",
    description:
      "Rastreador de follows de Instagram para ver a quién siguió recientemente un perfil público. Compara la actividad sin compartir tu contraseña.",
    keywords: [
      "rastrear follows recientes instagram",
      "rastreador de follows instagram",
      "ver a quién siguió recientemente alguien",
      "rastreador de unfollows instagram",
      "ver actividades instagram",
    ],
    canonical: "https://stalkie.app/",
    ogTitle: "Stalkie — Rastreador de Follows de Instagram | Mira Follows Recientes",
    ogDescription:
      "Rastreador de follows de Instagram para ver a quién siguió recientemente un perfil público. Compara la actividad sin compartir tu contraseña.",
    ogImage: "https://stalkie.app/og.png",
    twitterCard: "summary_large_image",
  },
  header: {
    logo: "Stalkie",
    links: [
      { label: "Cómo funciona", href: "#how-it-works" },
      { label: "Opiniones", href: "#reviews" },
      { label: "FAQ", href: "#faq" },
    ],
    loginLabel: "Iniciar sesión",
    ctaLabel: "Comenzar",
  },
  hero: {
    badge: "RASTREO 100% ANÓNIMO",
    tiktokBadge: "visto en tiktok",
    liveText: "personas buscando ahora",
    title: "Mira a quién siguieron recientemente en Instagram",
    subtitle:
      "Mira a quién sigue tu pareja, crush o amigos recientemente. Recibe alertas de nuevos follows y unfollows. 100% anónimo.",
    inputPlaceholder: "Ingresa el @usuario",
    ctaLabel: "Analizar",
    helperText: "Sin contraseña de Instagram. Búsqueda 100% anónima.",
    trustBullets: [
      "100% Anónimo",
      "Sin iniciar sesión",
      "Línea de tiempo",
      "Detecta follows sospechosos",
    ],
    socialProof: "Miles de búsquedas realizadas",
  },
  qualitativeMetrics: [
    { label: "Búsqueda rápida" },
    { label: "Sin contraseña" },
    { label: "Línea de tiempo" },
  ],
  metrics: {
    publicSearches: "Búsquedas Públicas",
    countries: "Países",
    passwordsRequired: "Contraseñas Requeridas",
  },
  howItWorks: {
    eyebrow: "Cómo funciona",
    title: "Rastrea cada movimiento, automáticamente.",
    subtitle:
      "Cómo Stalkie monitorea las conexiones sin necesidad de tu login.",
    step2: {
      scanning: "Escaneando...",
      newBadge: "NUEVO",
      timeAgo: "hace 2h",
    },
    step3Events: [
      {
        type: "a",
        avatar: "ER",
        avatarBg: "#FF6B9D",
        time: "Justo ahora",
        text: "Comenzó a seguir a @emily.rose",
      },
      {
        type: "b",
        avatar: "JP",
        avatarBg: "#C084FC",
        time: "Hace 2 horas",
        text: "Dejó de seguir a @jake_photo",
      },
      {
        type: "c",
        avatar: "LF",
        avatarBg: "#34D399",
        time: "Hoy",
        text: "Comenzó a seguir a @lisa_fit",
      },
      {
        type: "a",
        avatar: "SM",
        avatarBg: "#FB923C",
        time: "Ayer",
        text: "Comenzó a seguir a @sofia.m",
      },
      {
        type: "b",
        avatar: "NS",
        avatarBg: "#60A5FA",
        time: "Ayer",
        text: "Dejó de seguir a @nina.s",
      },
      {
        type: "c",
        avatar: "AT",
        avatarBg: "#34D399",
        time: "Hace 2 días",
        text: "Comenzó a seguir a @alex_t",
      },
    ],
    steps: [
      {
        badge: "PASO 1",
        title: "Ingresa el @usuario",
        description: "Escribe el perfil de Instagram que deseas analizar.",
      },
      {
        badge: "PASO 2",
        title: "Ver follows recientes",
        description:
          "Nuestro sistema mapea las conexiones más recientes en una línea de tiempo limpia.",
      },
      {
        badge: "PASO 3",
        title: "Recibe alertas de cambios",
        description:
          "Entérate de inmediato cada vez que el perfil siga o deje de seguir a alguien.",
      },
    ],
  },
  productPreview: {
    title: "Detecta señales de alerta al instante",
    subtitle:
      "Obtén una línea de tiempo clara mostrando a quién siguieron, cuándo y sus detalles de perfil.",
    profile: {
      handle: "@usuario",
      type: "Feed de Actividades",
    },
    tabs: ["Recientes", "Siguiendo", "Seguidores"],
    events: [
      {
        badge: "Nuevo Follow",
        title: "Comenzó a seguir a @katie.xo",
        time: "Justo ahora",
        avatar: "KX",
      },
      {
        badge: "Nuevo Follow",
        title: "Comenzó a seguir a @lucas_fit",
        time: "Hace 10 min",
        avatar: "LF",
      },
      {
        badge: "Unfollow",
        title: "Dejó de seguir a @fitness_coach",
        time: "Hace 2h",
        avatar: "FC",
      },
      {
        badge: "Alerta",
        title: "Cantidad de seguidos cambió (+2)",
        time: "Hoy",
        avatar: "FC",
      },
      {
        badge: "Línea de tiempo",
        title: "Línea de tiempo actualizada",
        time: "Hace 2 días",
        avatar: "TR",
      },
    ],
    disclaimer:
      "Solo revisamos perfiles públicos. Perfiles privados no pueden ser monitoreados.",
  },
  socialIntent: {
    title: "Las preguntas que te has estado haciendo",
    subtitle: "Stalkie te da las respuestas que mereces en segundos.",
    cards: [
      {
        title: "¿Quién es ese nuevo follow?",
        text: "Mira nuevos follows al instante sin tener que revisar Instagram todo el día.",
      },
      {
        title: "¿Siguió y luego dejó de seguir?",
        text: "Monitorea fluctuaciones y actividad oculta en su lista de seguidos.",
      },
      {
        title: "¿Tu pareja esconde algo?",
        text: "Obtén pruebas de a quién siguieron recientemente, con fecha y hora.",
      },
      {
        title: "¿Qué cambió desde ayer?",
        text: "Compara la actividad del perfil hoy verus ayer de forma automática.",
      },
      {
        title: "¿Están hablando con alguien nuevo?",
        text: "Recibe una notificación cada vez que hagan una nueva conexión.",
      },
      {
        title: "Deja de adivinar",
        text: "Ten una línea de tiempo clara en lugar de intentar recordar las listas de seguidos.",
      },
    ],
  },
  timeline: {
    title: "Línea de tiempo de a quién siguen",
    subtitle: "Actividad reciente organizada por perfil y hora.",
    events: [
      {
        badge: "NUEVO FOLLOW",
        title: "Comenzó a seguir a @katie.xo",
        time: "Hace 2 min",
        avatar: "KX",
      },
      {
        badge: "NUEVO FOLLOW",
        title: "Comenzó a seguir a @lucas_fit",
        time: "Hace 15 min",
        avatar: "LF",
      },
      {
        badge: "ACTUALIZACIÓN",
        title: "Cantidad de seguidos cambió",
        time: "Hace 1h",
        avatar: "FC",
      },
      {
        badge: "UNFOLLOW",
        title: "Dejó de seguir a @fitness_coach",
        time: "Hace 4h",
        avatar: "FC",
      },
      {
        badge: "LÍNEA DE TIEMPO",
        title: "Actualizada hoy",
        time: "Hace 5h",
        avatar: "RT",
      },
    ],
  },
  socialProofGrid: {
    title: "La gente usa Stalkie cuando siente que algo anda mal",
    subtitle: "Situaciones reales. Líneas de tiempo claras. Deja de adivinar.",
    reviews: [
      {
        name: "Maya",
        text: "Solo quería saber qué había cambiado. La línea de tiempo lo dejó claro sin iniciar sesión en Instagram.",
        rating: 5,
        badge: "",
      },
      {
        name: "Alex T.",
        text: "Mucho más fácil que revisar manualmente.",
        rating: 5,
        badge: "",
      },
      {
        name: "Jess R.",
        text: "Encontré los follows recientes en segundos.",
        rating: 5,
        badge: "",
      },
      {
        name: "Mike P.",
        text: "No requiere inicio de sesión, por eso confié.",
        rating: 5,
        badge: "",
      },
      {
        name: "Emily L.",
        text: "Limpio y directo al grano.",
        rating: 5,
        badge: "",
      },
      {
        name: "David C.",
        text: "Mejor que actualizar Instagram una y otra vez.",
        rating: 4,
        badge: "",
      },
      {
        name: "Lucas B.",
        text: "Por fin pude dejar de adivinar.",
        rating: 5,
        badge: "",
      },
      {
        name: "Amanda K.",
        text: "Organizó todo lo que estaba intentando rastrear.",
        rating: 5,
        badge: "",
      },
      {
        name: "Clara V.",
        text: "Búsqueda simple, resultado claro.",
        rating: 4,
        badge: "",
      },
      {
        name: "Thiago F.",
        text: "La línea de tiempo pública fue suficiente.",
        rating: 5,
        badge: "",
      },
      {
        name: "Sophia G.",
        text: "Me encantó que nunca me pidió mi contraseña.",
        rating: 5,
        badge: "",
      },
      {
        name: "Rafael S.",
        text: "Mucho mejor que revisar perfiles uno por uno.",
        rating: 4,
        badge: "",
      },
    ],
  },
  benefits: {
    title: "Deja de adivinar. Descubre la verdad.",
    subtitle:
      "Monitoreo automático diseñado para relaciones, creadores y curiosidad.",
    cards: [
      {
        title: "Detecta follows sospechosos",
        description:
          "Mira nuevas conexiones en cuanto ocurren en un feed simple.",
      },
      {
        title: "Detecta unfollows secretos",
        description: "Entérate cuando limpian su lista o eliminan a alguien.",
      },
      {
        title: "Actualizaciones de seguidores",
        description: "Rastrea cualquier cambio en la lista de seguidores.",
      },
      {
        title: "Notificaciones en tiempo real",
        description: "Recibe actualizaciones cuando cambie la actividad.",
      },
      {
        title: "Cero esfuerzo",
        description: "No más capturas de pantalla manuales ni comparar listas.",
      },
    ],
  },
  comparison: {
    eyebrow: "Comparar",
    title: "Cómo se compara Stalkie al chequeo manual",
    subtitle: "Deja de buscar manualmente y permite que Stalkie se encargue.",
    columns: ["Característica", "Stalkie", "Manual"],
    rows: [
      ["Completamente Anónimo", "Sí, Siempre", "No / Dudoso"],
      ["Login de Instagram", "No Requerido", "Requerido"],
      ["Detección de Follows Sospechosos", "Automático", "Adivinación Manual"],
      ["Orden de Follows", "Recientes primero", "Aleatorio por IG"],
      ["Género del Perfil", "Incluido", "No disponible"],
      ["Actualizaciones", "Cada Hora", "Manual"],
      ["Notificaciones", "Push Instantáneo", "Ninguna"],
      ["Perfiles Monitoreados", "Hasta 3", "1 a la vez"],
      ["Anuncios", "Sin Anuncios", "Anuncios en todas partes"],
      ["Dispositivos", "Cualquier Dispositivo", "Solo PC"],
    ],
  },
  privacy: {
    title: "100% Anónimo. Sin Contraseña.",
    subtitle:
      "Stalkie está hecho para búsquedas seguras. Nunca te pediremos tus credenciales de Instagram.",
    items: [
      {
        title: "Sin iniciar sesión",
        description:
          "Busca de forma anónima sin conectar tu cuenta de Instagram.",
      },
      {
        title: "Seguro y privado",
        description: "El perfil objetivo nunca sabrá que lo buscaste.",
      },
      {
        title: "Reglas de perfil",
        description: "Solo se pueden rastrear perfiles públicos.",
      },
    ],
    helperText:
      "Perfiles privados no pueden ser rastreados. Solo monitoreamos listas públicas.",
  },
  faq: {
    title: "Preguntas frecuentes",
    subtitle: "Preguntas frecuentes sobre el monitoreo de Stalkie.",
    items: [
      {
        question: "¿Necesito iniciar sesión con Instagram?",
        answer:
          "No. Stalkie nunca te pedirá tu contraseña de Instagram ni tus datos de cuenta.",
      },
      {
        question: "¿Puede Stalkie chequear cuentas privadas?",
        answer:
          "No. Stalkie solo funciona con perfiles públicos de Instagram. Las cuentas privadas están protegidas.",
      },
      {
        question: "¿Sabrán que los estoy rastreando?",
        answer:
          "No. Stalkie es 100% anónimo. Chequeamos los perfiles desde nuestros servidores, por lo que nunca recibirán avisos.",
      },
      {
        question: "¿Qué puedo rastrear?",
        answer:
          "Puedes ver cambios recientes de seguidos, unfollows y cambios de conteo de seguidores en un solo lugar.",
      },
      {
        question: "¿Por qué algunos resultados no están disponibles?",
        answer:
          "La disponibilidad depende de los servidores de Instagram. Si un perfil no se puede chequear, te lo mostraremos de inmediato.",
      },
      {
        question: "¿Almacenan mi contraseña de Instagram?",
        answer:
          "No. Nunca te pediremos tu contraseña, por lo que no tenemos nada que almacenar.",
      },
    ],
  },
  trending: {
    eyebrow: "Tendencias",
    title: "El rumor se está corriendo",
    subtitle:
      "Miles de personas revisan perfiles públicos todos los días con Stalkie.",
    quotes: [
      {
        platform: "instagram",
        user: "@maya_c",
        text: "No sabía que el rastreo de seguidos funcionaba tan bien. Limpio y rápido.",
      },
      {
        platform: "tiktok",
        user: "@jess.r",
        text: "Sin iniciar sesión. Buscó en exactamente 10 segundos.",
      },
      {
        platform: "twitter",
        user: "@alex_t",
        text: "Por fin algo que no me pide mi contraseña de Instagram.",
      },
      {
        platform: "instagram",
        user: "@luca_b",
        text: "La línea de tiempo organizó todo lo que estaba intentando rastrear.",
      },
      {
        platform: "tiktok",
        user: "@sofia.m",
        text: "Solo actividad pública. Realmente confío en esto.",
      },
      {
        platform: "twitter",
        user: "@mike_p",
        text: "Mucho más fácil que revisar la lista manualmente.",
      },
      {
        platform: "instagram",
        user: "@liam.k",
        text: "Se lo conté a mi amiga y pilló a su novio siguiendo a su ex en 2 min.",
      },
      {
        platform: "tiktok",
        user: "@clara_v",
        text: "El rastreador de unfollows es brutal. Descubrí quién me dejó de seguir al instante.",
      },
      {
        platform: "twitter",
        user: "@dan_s",
        text: "Sin extensiones ni descargas raras. Solo busqué y obtuve los resultados.",
      },
      {
        platform: "instagram",
        user: "@emily_rose",
        text: "Reviso esto a diario solo para ver quién sigue a quién. Muy adictivo.",
      },
      {
        platform: "tiktok",
        user: "@hannah.b",
        text: "Realmente funciona y no te llena de spam.",
      },
      {
        platform: "twitter",
        user: "@sam_w",
        text: "La mejor herramienta para ver actividad pública sin iniciar sesión.",
      },
    ],
  },
  finalCta: {
    title: "¿Listo para saber la verdad?",
    subtitle:
      "Ingresa el @usuario abajo para revelar los cambios recientes y detectar actividad sospechosa.",
    inputPlaceholder: "Ingresa el @usuario",
    ctaLabel: "Analizar",
    trustBullets: [
      "Sin login de Instagram",
      "100% Anónimo",
      "Línea de tiempo clara",
    ],
  },
  guides: {
    eyebrow: "Guías",
    title: "Entiende mejor la actividad de Instagram",
    subtitle:
      "Guías prácticas sobre follows, unfollows y monitoreo de perfiles públicos.",
    readMore: "Leer guía",
    items: [
      {
        title: "Cómo ver a quién alguien siguió recientemente en Instagram",
        description:
          "Descubre qué muestra Instagram y cómo seguir nuevos follows con el tiempo.",
        href: "/es/como-ver-a-quien-alguien-siguio-en-instagram/",
      },
      {
        title: "Rastreador de seguidores de Instagram",
        description:
          "Aprende cómo detectar cambios sin compartir tu inicio de sesión.",
        href: "/es/rastreador-de-seguidores-instagram/",
      },
      {
        title: "Rastrear actividad de seguidos en Instagram",
        description:
          "Conoce cómo funciona el monitoreo anónimo de perfiles públicos.",
        href: "/es/rastrear-actividad-de-seguidos-instagram/",
      },
      {
        title: "Rastreador anónimo de Instagram",
        description:
          "Una guía centrada en la privacidad para seguir actividad pública.",
        href: "/es/rastreador-anonimo-instagram/",
      },
      {
        title: "Cómo recibir notificaciones de nuevos follows en Instagram",
        description:
          "Entiende las alertas de follow y monitorea cambios en perfiles públicos.",
        href: "/es/notificaciones-nuevos-seguidos-instagram/",
      },
      {
        title: "Rastreador de unfollows en Instagram",
        description:
          "Descubre qué puede confirmar el seguimiento de unfollows con datos públicos.",
        href: "/es/rastreador-de-unfollows-instagram/",
      },
      {
        title: "Seguir cambios en la lista de seguidos de Instagram",
        description:
          "Una guía práctica para comparar cambios en listas públicas de seguidos.",
        href: "/es/cambios-en-la-lista-de-seguidos-instagram/",
      },
    ],
  },
  footer: {
    description:
      "Stalkie te ayuda a rastrear la actividad reciente de Instagram, detectar follows sospechosos y dejar de adivinar.",
    productTitle: "Producto",
    companyTitle: "Compañía",
    legalTitle: "Legal",
    allRightsReserved: "Todos los derechos reservados.",
    productLinks: [
      { label: "Características", href: "#features" },
      { label: "Cómo funciona", href: "#how-it-works" },
      { label: "FAQ", href: "#faq" },
      { label: "Guías", href: "#guides" },
    ],
    companyLinks: [
      { label: "Contacto", href: "mailto:support@stalkie.app" },
      { label: "Soporte", href: "mailto:support@stalkie.app" },
    ],
    legalLinks: [
      { label: "Política de Privacidad", href: "/privacy" },
      { label: "Términos de Uso", href: "/terms" },
    ],
    disclaimer:
      "Stalkie no está afiliado a Instagram ni a Meta. Instagram es una marca registrada de sus respectivos propietarios.",
  },
};

export const landingContentByLocale = {
  en: enContent,
  pt: ptContent,
  es: esContent,
};

const contentMap = landingContentByLocale;

export const landingContent = new Proxy({} as typeof enContent, {
  get(_target, prop) {
    const locale = getActiveLocale();
    const content = contentMap[locale] || enContent;
    return Reflect.get(content, prop);
  },
});

export type LandingContent = typeof enContent;
export type LandingLocale = keyof typeof landingContentByLocale;

export function getLandingContentForLocale(
  locale: LandingLocale,
): LandingContent {
  return contentMap[locale] as LandingContent;
}

export function getLandingContent(): LandingContent {
  return landingContent;
}
